import { message, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useAddLeasingRegisterMutation, useDeleteLeasingRegisterMutation, useEditLeasingRegisterMutation } from "../../../../redux/api/mechanization/leasingRegisterApi";
import { getCookie } from "typescript-cookie";
import { EntLeasingRegister } from "../../../../models/mechanization/leasingRegister";

const useLeasingRegister = (modal: any, refresh?: () => void) => {
    const { t } = useTranslation();
    const { confirm } = modal;
    const [addLeasingRegister] = useAddLeasingRegisterMutation();
    const [deleteLeasingRegister] = useDeleteLeasingRegisterMutation();
    const [editLeasingRegister] = useEditLeasingRegisterMutation();

    async function onAddLeasingRegister(newLeasingRegister: EntLeasingRegister): Promise<boolean> {
        try {
            let status: boolean = false;
            await addLeasingRegister(newLeasingRegister).then((response: any) => {
                if (response.error !== undefined) {
                    const isKyrgyz = getCookie('i18next') === 'ky';
                    const lang = isKyrgyz ? 'kg' : 'ru';
                    const res = response.error.data?.res;
                    const errorMessages = Array.isArray(res)
                        ? res.map((error: any) => error[lang] || error.ru).join('\n')
                        : response.error.data?.msg || t('unknownError'); 
                    throw new Error(errorMessages);
                }
                if (response.data.success) {
                    status = true;
                    message.success(t('msg.successAdd'));
                    if (refresh) refresh();
                }
            }).catch((error: any) => {
                modal.error({ title: t('msg.error'), content: String(error.message) });
            });
            return status;
        } catch (error: any) {
            modal.error({ title: t('msg.error'), content: String(error.message) });
            return false;
        }
    }

    async function onDeleteLeasingRegister(leasingRegister: EntLeasingRegister) {
        try {
            let status: boolean = false;
            confirm({
                centered: true,
                title: t('msg.confirmDeleteText'),
                okText: t('general.yes'),
                cancelText: t('general.no'),
                onOk() {
                    deleteLeasingRegister(leasingRegister.id as number).then((response: any) => {
                        if (response.error) {
                            const isKyrgyz = getCookie('i18next') === 'ky';
                            const lang = isKyrgyz ? 'kg' : 'ru';
                            const res = response.error.data?.res;
                            const errorMessages = Array.isArray(res)
                                ? res.map((error: any) => error[lang] || error.ru).join('\n')
                                : response.error.data?.msg || t('unknownError'); 
                            throw new Error(errorMessages);
                        }
                        if (response.data.success) {
                            status = true;
                            Modal.success({ title: t('msg.successDelete') });
                            if (refresh) refresh();
                        }
                    }).catch((error: any) => {
                        Modal.error({ title: t('msg.error'), content: String(error.message) });
                    });
                }
            });
            return status;
        } catch (error: any) {
            Modal.error({ title: t("msg.error"), content: String(error.message) });
            return false;
        }
    };

    async function onEditLeasingRegister(editedLeasingRegister: EntLeasingRegister): Promise<boolean> {
        try {
            let status: boolean = false;
            await editLeasingRegister(editedLeasingRegister).then((response: any) => {
                if (response.error) {
                    const isKyrgyz = getCookie('i18next') === 'ky';
                    const lang = isKyrgyz ? 'kg' : 'ru';
                    const res = response.error.data?.res;
                    const errorMessages = Array.isArray(res)
                        ? res.map((error: any) => error[lang] || error.ru).join('\n')
                        : response.error.data?.msg || t('unknownError'); 
                    throw new Error(errorMessages);
                }
                if (response.data.success) {
                    status = true;
                    Modal.success({ title: t('msg.successEdited') });
                    if (refresh) refresh();
                }
            }).catch((error: any) => {
                Modal.error({ title: t('msg.error'), content: String(error.message) });
            });
            return status;
        } catch (error: any) {
            Modal.error({ title: t("msg.error"), content: String(error.message) });
            return false;
        }
    };

    return { onAddLeasingRegister, onDeleteLeasingRegister, onEditLeasingRegister };
};

export { useLeasingRegister };
